import CampaignIcon from '@mui/icons-material/Campaign';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getAccessToken } from '../../../app/Slices/AuthSlice';
import { useGetReleaseSettingsQuery } from '../../../app/Slices/SevenDayApiSlice';
import {
    Box,
    darken,
    lighten,
    Paper,
    Slide,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import UserRole from '../../../shared/AuthRoles';
import {
    acknowledgeRelease,
    shouldShowReleasePrompt,
} from '../../../shared/releaseNotes';

export default function ReleaseNotesNudge() {
    const theme = useTheme();
    const accessToken = useSelector(getAccessToken);
    const loggedInUser = UserRole();
    const prefersReducedMotion = useMediaQuery(
        '(prefers-reduced-motion: reduce)',
    );

    const { data: release, isLoading } = useGetReleaseSettingsQuery(undefined, {
        skip: !accessToken,
    });

    const [open, setOpen] = useState(false);
    const [show, setShow] = useState(false);

    useEffect(() => {
        if (!release) return;
        setOpen(shouldShowReleasePrompt(release, loggedInUser?.role));
    }, [release, loggedInUser?.role]);

    useEffect(() => {
        if (!open) {
            setShow(false);
            return;
        }
        if (prefersReducedMotion) {
            setShow(true);
            return;
        }
        const t = setTimeout(() => setShow(true), 3000);
        return () => clearTimeout(t);
    }, [open, prefersReducedMotion]);

    if (!accessToken || isLoading || !release || !open) return null;

    const onDismiss = () => {
        acknowledgeRelease(release.releasedAt, 'dismissed');
        setOpen(false);
    };

    const onViewFeatures = () => {
        acknowledgeRelease(release.releasedAt, 'viewed');
        setOpen(false);
        window.open(release.releaseNotesUrl, '_blank', 'noopener,noreferrer');
    };

    return (
        <Slide direction='up' in={show} mountOnEnter unmountOnExit>
            <Box
                sx={{
                    position: 'fixed',
                    left: 'var(--owner-drawer-width, 0px)',
                    right: 0,
                    bottom: 0,
                    zIndex: 1400,
                    transition: 'left 0.2s ease',
                    px: { xs: 1, sm: 2, md: 3 },
                    pb: {
                        xs: 'max(8px, env(safe-area-inset-bottom, 0px))',
                        sm: 'max(12px, env(safe-area-inset-bottom, 0px))',
                    },
                    pt: 0,
                    pointerEvents: 'none',
                }}
            >
                <Paper
                    variant='nudge'
                    aria-live='polite'
                    aria-label='New release notification'
                >
                    <Stack
                        direction='row'
                        spacing={1}
                        alignItems='center'
                        sx={{ minWidth: 0, flex: 1 }}
                    >
                        <CampaignIcon
                            sx={{
                                color: darken(theme.palette.yellow.main, 0.2),
                                fontSize: 18,
                                flexShrink: 0,
                                transformOrigin: '70% 70%',
                                ...(prefersReducedMotion
                                    ? null
                                    : {
                                          '@keyframes announcementWiggle': {
                                              '0%': {
                                                  transform: 'rotate(0deg)',
                                              },
                                              '6%': {
                                                  transform: 'rotate(-12deg)',
                                              },
                                              '12%': {
                                                  transform: 'rotate(10deg)',
                                              },
                                              '18%': {
                                                  transform: 'rotate(-10deg)',
                                              },
                                              '24%': {
                                                  transform: 'rotate(8deg)',
                                              },
                                              '30%': {
                                                  transform: 'rotate(0deg)',
                                              },
                                              '100%': {
                                                  transform: 'rotate(0deg)',
                                              },
                                          },
                                          animation:
                                              'announcementWiggle 2.8s ease-in-out infinite',
                                      }),
                            }}
                        />

                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontSize: 13,
                                    fontWeight: 800,
                                    color: darken(
                                        theme.palette.yellow.main,
                                        0.35,
                                    ),
                                    lineHeight: 1.2,
                                }}
                            >
                                {release.title}
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: darken(
                                        theme.palette.yellow.main,
                                        0.35,
                                    ),
                                    opacity: 0.9,
                                    lineHeight: 1.2,
                                    mt: 0.2,
                                }}
                            >
                                {release.message}
                            </Typography>
                        </Box>
                    </Stack>

                    <Stack
                        direction='row'
                        spacing={1}
                        sx={{
                            width: { xs: '100%', sm: 'auto' },
                            justifyContent: 'flex-end',
                        }}
                    >
                        <CustomButton
                            variant='outlined'
                            color='inherit'
                            size='small'
                            onClick={onDismiss}
                            sx={{
                                minWidth: 110,
                                backgroundColor: theme.palette.common.white,
                            }}
                        >
                            Dismiss
                        </CustomButton>
                        <CustomButton
                            variant='contained'
                            color='primary'
                            size='small'
                            onClick={onViewFeatures}
                            sx={{ minWidth: 110, fontWeight: 800 }}
                        >
                            See what’s new
                        </CustomButton>
                    </Stack>
                </Paper>
            </Box>
        </Slide>
    );
}
